import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ErrorNotification } from '@mr-yum/frontend-ui'
import { InputField } from 'components/HookForm/InputField'
import { FloatingButton } from 'components/Shared/FloatingButton'
import { useLogger } from 'hooks/useLogger'
import {
  useAddGuestMembershipMutation,
  useOptInToLoyaltyMutation,
  useUpdateCustomerDetailsMutation,
} from 'lib/gql'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import * as Yup from 'yup'

const getValidationSchema = ({ formatMessage }: IntlShape) =>
  Yup.object()
    .noUnknown(false)
    .shape({
      name: Yup.string().required(
        formatMessage({
          defaultMessage: 'Your name is required',
          id: '1KxtyA',
        }),
      ),
      email: Yup.string()
        .email(
          formatMessage({
            id: 'qDmele',
            defaultMessage: 'Email must be valid',
          }),
        )
        .required(
          formatMessage({
            id: 'gtleS5',
            defaultMessage: 'Please provide your email address',
          }),
        ),
    })

type FormValues = {
  email: string
  name: string
}

interface Props {
  name?: string | null
  email?: string | null
  organizationId: string
  venueSlug: string
  onSuccess?(): void
  header?: string | null
  body?: string | null
}

export const LoyaltyMembershipForm = ({
  name,
  email,
  organizationId,
  venueSlug,
  onSuccess = () => false,
  header,
  body,
}: Props) => {
  const { logEvent } = useLogger()
  const intl = useIntl()
  const defaultValues = useMemo(
    () => ({
      name: name ?? '',
      email: email ?? '',
    }),
    [name, email],
  )

  const form = useForm<FormValues>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(intl)),
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const [{ error: optInError }, sync] = useOptInToLoyaltyMutation()
  const [{ error: updateError }, updateUser] =
    useUpdateCustomerDetailsMutation()
  const [{ error: addError }, add] = useAddGuestMembershipMutation()

  const submitHandler = useCallback<SubmitHandler<FormValues>>(
    async ({ name, email }) => {
      logEvent('Clicked useLogger on membership loyalty')
      const { error: updateErr } = await updateUser({ name, email, venueSlug })
      if (updateErr) {
        return
      }
      const { error: syncErr } = await sync({ venueSlug })
      if (syncErr) {
        return
      }
      const { error: addErr } = await add({ organizationId })
      if (addErr) {
        return
      }

      onSuccess()
    },
    [updateUser, venueSlug, sync, organizationId, add, logEvent, onSuccess],
  )

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="m-9 pt-[calc(50vh-258px)]">
          <div>
            {optInError && <ErrorNotification fullWidth error={optInError} />}
            {updateError && <ErrorNotification fullWidth error={updateError} />}
            {addError && <ErrorNotification fullWidth error={addError} />}

            <h1 className="my-4 my-label-md">
              {header || (
                <FormattedMessage
                  defaultMessage="Join membership"
                  id="zmoGKL"
                />
              )}
            </h1>

            <p className="pragraph-md mb-5 text-foreground-subtle">
              {body || (
                <FormattedMessage
                  defaultMessage="Enter your name and email address to get access to our loyalty program and exclusive deals."
                  id="WFEBza"
                />
              )}
            </p>

            <InputField
              autoFocus
              name="name"
              data-testid="name"
              label={intl.formatMessage({
                defaultMessage: 'Full name',
                id: 'yk4PT9',
              })}
            />

            <InputField
              name="email"
              data-testid="email"
              label={intl.formatMessage({
                defaultMessage: 'Email address',
                id: 'hJZwTS',
              })}
            />
          </div>
        </div>

        <FloatingButton>
          <Button
            data-testid="loyalty-form-submit"
            isLoading={isSubmitting}
            type="submit"
            size="lg"
            fullWidth
          >
            <FormattedMessage defaultMessage="Sign up" id="8HJxXG" />
          </Button>
        </FloatingButton>
      </form>
    </FormProvider>
  )
}
